import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, Calendar, User, Edit, MessageSquare, Send } from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { TaskComments } from "@/components/tasks/TaskComments";

interface SubtaskDetailModalProps {
  open: boolean;
  onClose: () => void;
  subtaskId: string;
  taskId: string;
  unaId: string | null;
  onUpdate: () => void;
  onEdit: () => void;
}

export function SubtaskDetailModal({ 
  open, 
  onClose, 
  subtaskId, 
  taskId, 
  unaId, 
  onUpdate,
  onEdit 
}: SubtaskDetailModalProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [subtask, setSubtask] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(false);
  const [comments, setComments] = useState<any[]>([]);
  const [newComment, setNewComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && subtaskId) {
      loadSubtask();
      loadComments();
    }
  }, [open, subtaskId]);

  const loadSubtask = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('task_subtasks')
        .select('*')
        .eq('id', subtaskId)
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        setSubtask(null);
        return;
      }

      let assignee = null as any;
      if (data.assignee_id) {
        const { data: member } = await supabase
          .from('una_members')
          .select('id, full_name, member_role, email')
          .eq('id', data.assignee_id)
          .maybeSingle();
        assignee = member || null;
      }

      setSubtask({ ...data, assignee });
    } catch (error) {
      console.error('Error loading subtask:', error);
      toast({
        title: "Error",
        description: "Failed to load subtask details",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async () => {
    try {
      const { data, error } = await supabase
        .from('task_comments')
        .select('*')
        .eq('task_id', taskId)
        .eq('subtask_id', subtaskId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const commentsWithProfiles = await Promise.all((data || []).map(async (c) => {
        const { data: profile } = await supabase
          .from('profiles')
          .select('full_name, avatar_url')
          .eq('id', c.user_id)
          .maybeSingle();
        return {
          ...c,
          commenter: { full_name: profile?.full_name, avatar_url: profile?.avatar_url }
        };
      }));

      setComments(commentsWithProfiles);
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !user || !unaId) return;

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('task_comments')
        .insert({
          task_id: taskId,
          subtask_id: subtaskId,
          una_id: unaId,
          user_id: user.id,
          comment: newComment.trim(),
        });

      if (error) throw error;

      toast({
        title: "Comment Added",
        description: "Your note has been saved",
      });

      setNewComment("");
      await loadComments();
    } catch (error) {
      console.error('Error adding comment:', error);
      toast({
        title: "Error",
        description: "Failed to add comment",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleComplete = async () => {
    if (!subtask) return;
    
    setToggling(true);
    const newStatus = subtask.status === 'completed' ? 'not_started' : 'completed';
    
    try {
      const { error } = await supabase
        .from('task_subtasks')
        .update({ status: newStatus })
        .eq('id', subtaskId);

      if (error) throw error;

      toast({
        title: "Subtask Updated",
        description: newStatus === 'completed' ? "Subtask marked as completed!" : "Subtask reopened",
      });

      await loadSubtask();
      onUpdate();
    } catch (error) {
      console.error('Error toggling subtask:', error);
      toast({
        title: "Error",
        description: "Failed to update subtask",
        variant: "destructive",
      });
    } finally {
      setToggling(false);
    }
  };

  if (loading || !subtask) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden">
          <div className="flex items-center justify-center py-8">
            Loading subtask details...
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const statusColors = {
    not_started: "bg-gray-500",
    in_progress: "bg-blue-500",
    completed: "bg-green-500",
    blocked: "bg-red-500",
  };

  const statusLabels = {
    not_started: "Not Started",
    in_progress: "In Progress",
    completed: "Completed",
    blocked: "Blocked",
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <DialogTitle className="text-xl">{subtask.title}</DialogTitle>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <Badge className={statusColors[subtask.status as keyof typeof statusColors]}>
                  {statusLabels[subtask.status as keyof typeof statusLabels]}
                </Badge>
                {subtask.assignee && (
                  <Badge variant="outline" className="gap-1">
                    <User className="h-3 w-3" />
                    {subtask.assignee.full_name}
                  </Badge>
                )}
                {subtask.due_date && (
                  <Badge variant="outline" className="gap-1">
                    <Calendar className="h-3 w-3" />
                    Due {format(new Date(subtask.due_date), 'MMM d, yyyy')}
                  </Badge>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={subtask.status === 'completed' ? "outline" : "default"}
                onClick={handleToggleComplete}
                disabled={toggling}
                className={subtask.status === 'completed' ? "" : "bg-green-600 hover:bg-green-700"}
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                {subtask.status === 'completed' ? 'Reopen' : 'Mark Complete'}
              </Button>
              <Button size="sm" variant="outline" onClick={onEdit}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
            </div>
          </div>
        </DialogHeader>

        <Separator />

        <div className="flex-1 overflow-y-auto">
          <Tabs defaultValue="details" className="w-full">
            <TabsList className="w-full justify-start">
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="notes">
                <MessageSquare className="h-4 w-4 mr-2" />
                Notes ({comments.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-4 mt-4">
              {subtask.description && (
                <div>
                  <h4 className="font-medium mb-2">Description</h4>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                    {subtask.description}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium mb-2">Status</h4>
                  <Badge className={statusColors[subtask.status as keyof typeof statusColors]}>
                    {statusLabels[subtask.status as keyof typeof statusLabels]}
                  </Badge>
                </div>

                {subtask.assignee && (
                  <div>
                    <h4 className="font-medium mb-2">Assigned To</h4>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{subtask.assignee.full_name}</span>
                    </div>
                  </div>
                )}

                {subtask.due_date && (
                  <div>
                    <h4 className="font-medium mb-2">Due Date</h4>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">
                        {format(new Date(subtask.due_date), 'MMMM d, yyyy')}
                      </span>
                    </div>
                  </div>
                )}

                <div>
                  <h4 className="font-medium mb-2">Created</h4>
                  <span className="text-sm text-muted-foreground">
                    {format(new Date(subtask.created_at), 'MMM d, yyyy h:mm a')}
                  </span>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="notes" className="mt-4 space-y-4">
              {unaId ? (
                <TaskComments taskId={taskId} unaId={unaId} subtaskId={subtaskId} />
              ) : (
                <div className="text-sm text-muted-foreground">Notes unavailable: UNA not set.</div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </DialogContent>
    </Dialog>
  );
}
